import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckSquare, FiPlus, FiX, FiEdit3, FiCalendar, FiClock, FiFlag, FiCheck, FiRefreshCw } = FiIcons;

const ActionPlan = ({ formData, updateFormData, setCurrentStep }) => {
  const navigate = useNavigate();
  const [actionPlan, setActionPlan] = useState(formData.actionPlan || {});
  const [newTask, setNewTask] = useState('');
  const [newImplementation, setNewImplementation] = useState('');
  const [selectedGoal, setSelectedGoal] = useState('');
  const [notification, setNotification] = useState('');
  const [scheduledTasks, setScheduledTasks] = useState({});
  const [editingTask, setEditingTask] = useState({ goalIndex: null, taskIndex: null, text: '', implementation: '' });
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverGoal, setDragOverGoal] = useState(null);

  const allGoals = [
    ...formData.monthlyGoals?.map(goal => ({ text: goal, timeframe: 'Monthly' })) || [],
    ...formData.sixMonthGoals?.map(goal => ({ text: goal, timeframe: '6-Month' })) || [],
    ...formData.yearlyGoals?.map(goal => ({ text: goal, timeframe: 'Yearly' })) || []
  ];

  useEffect(() => {
    const initialPlan = {};
    allGoals.forEach((goal, index) => {
      const goalKey = `goal_${index}`;
      if (!actionPlan[goalKey]) {
        initialPlan[goalKey] = generateSuggestedTasks(goal.text);
      }
    });
    if (Object.keys(initialPlan).length > 0) {
      setActionPlan(prev => ({ ...prev, ...initialPlan }));
    }
  }, []);

  useEffect(() => {
    // Initialize scheduled tasks from existing data
    if (formData.scheduledTasks) {
      setScheduledTasks(formData.scheduledTasks);
    }
  }, [formData.scheduledTasks]);

  const generateSuggestedTasks = (goalText) => {
    const taskSuggestions = {
      'email list': [
        { text: 'Create a lead magnet (free guide, checklist, or mini-course)', implementation: 'Use Canva to create a PDF guide, set up landing page with Mailchimp' },
        { text: 'Set up email signup forms on your website', implementation: 'Add Mailchimp signup forms to homepage and about page' },
        { text: 'Promote lead magnet on social media platforms', implementation: 'Create 3 posts per week for 2 weeks linking to lead magnet' },
        { text: 'Reach out to your existing network', implementation: 'Send personalized emails to 10 contacts per day for 2 weeks' }
      ],
      'students': [
        { text: 'Create referral incentive program for existing students', implementation: 'Offer 10% discount for each successful referral, create referral cards' },
        { text: 'Optimize your online presence (website, social media)', implementation: 'Update website SEO, post daily on Instagram/Facebook' },
        { text: 'Network with local schools and community centers', implementation: 'Contact 5 schools per week, offer free workshop' },
        { text: 'Offer free trial lessons or consultations', implementation: 'Create 30-minute consultation package, promote on social media' }
      ],
      'social media': [
        { text: 'Create content calendar with educational posts', implementation: 'Use Google Calendar to plan 30 days of content topics' },
        { text: 'Share student success stories (with permission)', implementation: 'Request testimonials monthly, create graphics with quotes' },
        { text: 'Post daily tips related to your subject', implementation: 'Create bank of 50 tips, schedule using Buffer/Hootsuite' },
        { text: 'Engage with followers through comments and messages', implementation: 'Set daily 30-minute block for responding to comments' }
      ],
      'course': [
        { text: 'Outline course curriculum and learning objectives', implementation: 'Use mind mapping tool, define 10 learning outcomes' },
        { text: 'Create course materials (videos, worksheets, etc.)', implementation: 'Record 2 videos per week, design worksheets in Canva' },
        { text: 'Set up course platform (Teachable, Thinkific, etc.)', implementation: 'Research platforms, choose one, upload first module' },
        { text: 'Plan course launch marketing campaign', implementation: 'Create email sequence, social media countdown, launch webinar' }
      ],
      'revenue': [
        { text: 'Analyze current pricing and adjust if needed', implementation: 'Research competitor pricing, create tiered pricing structure' },
        { text: 'Upsell existing students to premium packages', implementation: 'Identify top 20% students, offer personalized coaching' },
        { text: 'Create higher-value service offerings', implementation: 'Design premium packages with exclusive benefits' },
        { text: 'Track and optimize conversion rates', implementation: 'Set up Google Analytics, A/B test pricing pages' }
      ],
      'website': [
        { text: 'Research relevant keywords for your niche', implementation: 'Use SEMrush to find 20 keywords, analyze competition' },
        { text: 'Optimize page titles and descriptions', implementation: 'Update all page titles with target keywords' },
        { text: 'Create valuable blog content regularly', implementation: 'Write 2 blog posts per week, optimize for SEO' },
        { text: 'Build backlinks from education-related sites', implementation: 'Guest post on 5 education blogs monthly' }
      ]
    };

    const lowerGoal = goalText.toLowerCase();
    for (const [keyword, tasks] of Object.entries(taskSuggestions)) {
      if (lowerGoal.includes(keyword)) {
        return tasks.map(task => ({ 
          text: task.text, 
          implementation: task.implementation,
          week: 1, 
          month: 1,
          completed: false
        }));
      }
    }

    return [
      { text: 'Break down this goal into smaller milestones', implementation: 'Create project timeline with specific deliverables', week: 1, month: 1, completed: false },
      { text: 'Identify resources and tools needed', implementation: 'List all required tools, budget for purchases', week: 1, month: 1, completed: false },
      { text: 'Set weekly check-in points to track progress', implementation: 'Schedule weekly review meetings with accountability partner', week: 1, month: 1, completed: false },
      { text: 'Create accountability measures', implementation: 'Join mastermind group, hire coach, use tracking app', week: 1, month: 1, completed: false }
    ];
  };

  const addTask = (goalIndex) => {
    if (newTask.trim() && newImplementation.trim()) {
      const goalKey = `goal_${goalIndex}`;
      const newTaskObj = { 
        text: newTask.trim(), 
        implementation: newImplementation.trim(),
        week: 1, 
        month: 1,
        completed: false
      };
      setActionPlan(prev => ({
        ...prev,
        [goalKey]: [...(prev[goalKey] || []), newTaskObj]
      }));
      setNewTask('');
      setNewImplementation('');
      setSelectedGoal('');
    }
  };

  const removeTask = (goalIndex, taskIndex) => {
    const goalKey = `goal_${goalIndex}`;
    setActionPlan(prev => ({
      ...prev,
      [goalKey]: prev[goalKey].filter((_, i) => i !== taskIndex)
    }));
  };

  const toggleTaskCompletion = (goalIndex, taskIndex) => {
    const goalKey = `goal_${goalIndex}`;
    setActionPlan(prev => {
      const newPlan = { ...prev };
      const tasks = [...newPlan[goalKey]];
      tasks[taskIndex] = { ...tasks[taskIndex], completed: !tasks[taskIndex].completed };
      newPlan[goalKey] = tasks;
      return newPlan;
    });
  };

  const startEditingTask = (goalIndex, taskIndex) => {
    const goalKey = `goal_${goalIndex}`;
    const task = actionPlan[goalKey][taskIndex];
    setEditingTask({ 
      goalIndex, 
      taskIndex, 
      text: task.text,
      implementation: task.implementation || ''
    });
  };

  const saveTaskEdit = () => {
    if (editingTask.text.trim() && editingTask.implementation.trim()) {
      const goalKey = `goal_${editingTask.goalIndex}`;
      setActionPlan(prev => {
        const newPlan = { ...prev };
        const tasks = [...newPlan[goalKey]];
        tasks[editingTask.taskIndex] = { 
          ...tasks[editingTask.taskIndex], 
          text: editingTask.text.trim(),
          implementation: editingTask.implementation.trim()
        };
        newPlan[goalKey] = tasks;
        return newPlan;
      });
      setEditingTask({ goalIndex: null, taskIndex: null, text: '', implementation: '' });
    }
  };

  const cancelTaskEdit = () => {
    setEditingTask({ goalIndex: null, taskIndex: null, text: '', implementation: '' });
  };

  const updateTaskSchedule = (goalIndex, taskIndex, field, value) => {
    const goalKey = `goal_${goalIndex}`;
    setActionPlan(prev => {
      const newPlan = { ...prev };
      const tasks = [...newPlan[goalKey]];
      tasks[taskIndex] = { ...tasks[taskIndex], [field]: value };
      newPlan[goalKey] = tasks;
      return newPlan;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, goalIndex, taskIndex) => {
    setDraggedTask({ goalIndex, taskIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, goalIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGoal(goalIndex);
  };

  const handleDragLeave = () => {
    setDragOverGoal(null);
  };

  const handleDrop = (e, dropGoalIndex, dropTaskIndex = null) => {
    e.preventDefault();
    setDragOverGoal(null);

    if (!draggedTask) return;

    const { goalIndex: dragGoalIndex, taskIndex: dragTaskIndex } = draggedTask;

    // Don't do anything if dropping on the same position
    if (dragGoalIndex === dropGoalIndex && dragTaskIndex === dropTaskIndex) {
      setDraggedTask(null);
      return;
    }

    const dragGoalKey = `goal_${dragGoalIndex}`;
    const dropGoalKey = `goal_${dropGoalIndex}`;

    setActionPlan(prev => {
      const newPlan = { ...prev };
      
      // Get the dragged task
      const draggedTasks = [...newPlan[dragGoalKey]];
      const [draggedTask] = draggedTasks.splice(dragTaskIndex, 1);

      // If dropping on the same goal, reorder
      if (dragGoalIndex === dropGoalIndex) {
        // Calculate the new position
        const newPosition = dropTaskIndex !== null && dropTaskIndex < dragTaskIndex 
          ? dropTaskIndex 
          : dropTaskIndex !== null 
            ? dropTaskIndex + 1 
            : draggedTasks.length;
        
        draggedTasks.splice(newPosition, 0, draggedTask);
        newPlan[dragGoalKey] = draggedTasks;
      } else {
        // Moving to a different goal
        const dropTasks = [...(newPlan[dropGoalKey] || [])];
        const insertPosition = dropTaskIndex !== null ? dropTaskIndex : dropTasks.length;
        dropTasks.splice(insertPosition, 0, draggedTask);
        newPlan[dragGoalKey] = draggedTasks;
        newPlan[dropGoalKey] = dropTasks;
      }

      return newPlan;
    });

    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverGoal(null);
  };

  const generateScheduledCalendar = () => {
    const calendar = {
      monthly: {},
      sixMonth: {},
      yearly: {}
    };

    allGoals.forEach((goal, goalIndex) => {
      const goalKey = `goal_${goalIndex}`;
      const tasks = actionPlan[goalKey] || [];

      tasks.forEach(task => {
        const taskWithGoal = { 
          ...task, 
          goalText: goal.text, 
          timeframe: goal.timeframe 
        };

        if (goal.timeframe === 'Monthly') {
          // Monthly goals go into weeks 1-4 of month 1
          const weekKey = `week_${task.week || 1}`;
          if (!calendar.monthly[weekKey]) {
            calendar.monthly[weekKey] = [];
          }
          calendar.monthly[weekKey].push(taskWithGoal);
        } else if (goal.timeframe === '6-Month') {
          // 6-Month goals go into specified month and week
          const monthKey = `month_${task.month || 1}`;
          const weekKey = `${monthKey}_week_${task.week || 1}`;
          if (!calendar.sixMonth[weekKey]) {
            calendar.sixMonth[weekKey] = [];
          }
          calendar.sixMonth[weekKey].push(taskWithGoal);
        } else if (goal.timeframe === 'Yearly') {
          // Yearly goals go into quarters
          const quarter = Math.ceil((task.month || 1) / 3);
          const quarterKey = `quarter_${quarter}`;
          if (!calendar.yearly[quarterKey]) {
            calendar.yearly[quarterKey] = [];
          }
          calendar.yearly[quarterKey].push(taskWithGoal);
        }
      });
    });

    return calendar;
  };

  const handleNext = () => {
    const calendar = generateScheduledCalendar();
    updateFormData({ actionPlan, scheduledTasks: calendar, hasCalendarView: true });
    setCurrentStep(5);
    navigate('/marketing-analysis');
  };

  const handlePrevious = () => {
    setCurrentStep(3);
    navigate('/goals');
  };

  const handleStartAgain = () => {
    updateFormData({
      name: '',
      subject: '',
      experience: '',
      currentStudents: '',
      teachingMethod: '',
      targetAudience: '',
      niche: '',
      uniqueValue: '',
      targetMarket: '',
      monthlyGoals: [],
      sixMonthGoals: [],
      yearlyGoals: [],
      actionPlan: {},
      scheduledTasks: {}
    });
    setCurrentStep(0);
    navigate('/');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <SafeIcon icon={FiCheckSquare} className="text-2xl text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Create Your Action Plan</h2>
          <p className="text-gray-600">Organize your goals into actionable tasks and specify how you'll implement them</p>
        </div>

        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-blue-100 text-blue-800 rounded-lg text-center"
          >
            {notification}
          </motion.div>
        )}

        <div className="space-y-8">
          {allGoals.map((goal, index) => {
            const goalKey = `goal_${index}`;
            const tasks = actionPlan[goalKey] || [];

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`border rounded-lg p-6 transition-all ${
                  dragOverGoal === index ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                }`}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                <div className="mb-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                      {goal.timeframe}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">{goal.text}</h3>
                </div>

                {/* Task Headers */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 text-sm font-semibold text-gray-600">
                  <div className="md:col-span-1">✓</div>
                  <div className="md:col-span-4">Task</div>
                  <div className="md:col-span-4">How to Implement</div>
                  <div className="md:col-span-2">Schedule</div>
                  <div className="md:col-span-1">Actions</div>
                </div>

                <div className="space-y-3 mb-4">
                  {tasks.map((task, taskIndex) => (
                    <motion.div
                      key={taskIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index, taskIndex)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, index, taskIndex)}
                      className={`grid grid-cols-1 md:grid-cols-12 gap-4 items-center p-3 rounded-lg transition-all cursor-move ${
                        draggedTask?.goalIndex === index && draggedTask?.taskIndex === taskIndex
                          ? 'opacity-50 bg-gray-100'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {/* Checkbox */}
                      <div className="md:col-span-1">
                        <input
                          type="checkbox"
                          checked={task.completed || false}
                          onChange={() => toggleTaskCompletion(index, taskIndex)}
                          className="w-4 h-4 border-2 border-gray-300 rounded cursor-pointer hover:border-blue-500"
                        />
                      </div>

                      {/* Task Text */}
                      <div className="md:col-span-4">
                        {editingTask.goalIndex === index && editingTask.taskIndex === taskIndex ? (
                          <input
                            type="text"
                            value={editingTask.text}
                            onChange={(e) => setEditingTask(prev => ({ ...prev, text: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && saveTaskEdit()}
                            autoFocus
                          />
                        ) : (
                          <span className={`text-gray-700 text-sm ${task.completed ? 'line-through opacity-60' : ''}`}>
                            {task.text}
                          </span>
                        )}
                      </div>

                      {/* Implementation */}
                      <div className="md:col-span-4">
                        {editingTask.goalIndex === index && editingTask.taskIndex === taskIndex ? (
                          <input
                            type="text"
                            value={editingTask.implementation}
                            onChange={(e) => setEditingTask(prev => ({ ...prev, implementation: e.target.value }))}
                            placeholder="How will you implement this?"
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && saveTaskEdit()}
                          />
                        ) : (
                          <span className={`text-gray-600 text-sm italic ${task.completed ? 'line-through opacity-60' : ''}`}>
                            {task.implementation || 'No implementation plan'}
                          </span>
                        )}
                      </div>

                      {/* Schedule */}
                      <div className="md:col-span-2 flex items-center space-x-2">
                        {goal.timeframe === 'Monthly' && (
                          <select
                            value={task.week || 1}
                            onChange={(e) => updateTaskSchedule(index, taskIndex, 'week', parseInt(e.target.value))}
                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value={1}>Week 1</option>
                            <option value={2}>Week 2</option>
                            <option value={3}>Week 3</option>
                            <option value={4}>Week 4</option>
                          </select>
                        )}

                        {(goal.timeframe === '6-Month' || goal.timeframe === 'Yearly') && (
                          <>
                            <select
                              value={task.month || 1}
                              onChange={(e) => updateTaskSchedule(index, taskIndex, 'month', parseInt(e.target.value))}
                              className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {goal.timeframe === '6-Month' ? (
                                <>
                                  <option value={1}>M1</option>
                                  <option value={2}>M2</option>
                                  <option value={3}>M3</option>
                                  <option value={4}>M4</option>
                                  <option value={5}>M5</option>
                                  <option value={6}>M6</option>
                                </>
                              ) : (
                                <>
                                  <option value={1}>M1</option>
                                  <option value={2}>M2</option>
                                  <option value={3}>M3</option>
                                  <option value={4}>M4</option>
                                  <option value={5}>M5</option>
                                  <option value={6}>M6</option>
                                  <option value={7}>M7</option>
                                  <option value={8}>M8</option>
                                  <option value={9}>M9</option>
                                  <option value={10}>M10</option>
                                  <option value={11}>M11</option>
                                  <option value={12}>M12</option>
                                </>
                              )}
                            </select>
                            <select
                              value={task.week || 1}
                              onChange={(e) => updateTaskSchedule(index, taskIndex, 'week', parseInt(e.target.value))}
                              className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value={1}>W1</option>
                              <option value={2}>W2</option>
                              <option value={3}>W3</option>
                              <option value={4}>W4</option>
                            </select>
                          </>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="md:col-span-1 flex items-center space-x-1">
                        {editingTask.goalIndex === index && editingTask.taskIndex === taskIndex ? (
                          <>
                            <button
                              onClick={saveTaskEdit}
                              className="p-1 text-green-600 hover:bg-green-100 rounded transition-all"
                              title="Save"
                            >
                              <SafeIcon icon={FiCheck} />
                            </button>
                            <button
                              onClick={cancelTaskEdit}
                              className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-all"
                              title="Cancel"
                            >
                              <SafeIcon icon={FiX} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingTask(index, taskIndex);
                              }}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-all"
                              title="Edit task"
                            >
                              <SafeIcon icon={FiEdit3} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeTask(index, taskIndex);
                              }}
                              className="p-1 text-red-500 hover:bg-red-100 rounded transition-all"
                              title="Remove task"
                            >
                              <SafeIcon icon={FiX} />
                            </button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Add New Task */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-1 text-center">
                      <div className="text-gray-400 text-sm">New</div>
                    </div>
                    <div className="md:col-span-4">
                      <input
                        type="text"
                        value={selectedGoal === goalKey ? newTask : ''}
                        onChange={(e) => {
                          setNewTask(e.target.value);
                          setSelectedGoal(goalKey);
                        }}
                        placeholder="Add a specific task..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && addTask(index)}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <input
                        type="text"
                        value={selectedGoal === goalKey ? newImplementation : ''}
                        onChange={(e) => {
                          setNewImplementation(e.target.value);
                          setSelectedGoal(goalKey);
                        }}
                        placeholder="How will you implement this?"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && addTask(index)}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <button
                        onClick={() => addTask(index)}
                        disabled={selectedGoal !== goalKey || !newTask.trim() || !newImplementation.trim()}
                        className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                          selectedGoal === goalKey && newTask.trim() && newImplementation.trim()
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <SafeIcon icon={FiPlus} className="inline mr-1" />
                        Add Task
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 mb-2">📅 Task Scheduling</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Monthly goals:</strong> Select which week (1-4) to schedule each task</li>
            <li>• <strong>6-Month goals:</strong> Select both month (1-6) and week (1-4) for each task</li>
            <li>• <strong>Yearly goals:</strong> Select month (1-12) and week (1-4) - tasks will be grouped by quarters</li>
            <li>• <strong>Implementation details:</strong> Be specific about how you'll execute each task</li>
            <li>• <strong>Drag & Drop:</strong> Drag tasks between goals to reorganize them</li>
            <li>• <strong>Auto-calendar:</strong> Tasks will be automatically organized in the next step</li>
          </ul>
        </div>

        <div className="mt-8 bg-yellow-50 rounded-lg p-6">
          <h3 className="font-semibold text-yellow-800 mb-2">💡 Implementation Tips</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• <strong>Be specific:</strong> Instead of "create content", write "create 3 Instagram posts about algebra basics using Canva"</li>
            <li>• <strong>Include tools:</strong> Mention specific tools you'll use (e.g., "use Mailchimp to create email campaign")</li>
            <li>• <strong>Set metrics:</strong> Define success criteria (e.g., "get 50 email subscribers")</li>
            <li>• <strong>Time-bound:</strong> Include when and how often (e.g., "post daily at 9 AM for 2 weeks")</li>
          </ul>
        </div>

        <div className="mt-8 flex justify-between">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
          >
            Previous
          </motion.button>
          <div className="flex space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStartAgain}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-all"
            >
              <SafeIcon icon={FiRefreshCw} className="inline mr-2" />
              Start Again
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all"
            >
              Generate Marketing Analysis
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ActionPlan;